/**
 * AWDD Theme — Main JavaScript
 * Uses GSAP + ScrollTrigger for animations
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { initDiagnostic } from './diagnostic.js';
import { initBlogFilter } from './blog.js';

gsap.registerPlugin(ScrollTrigger);

document.addEventListener('DOMContentLoaded', function () {

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ===== IT Maturity Diagnostic (interactive quiz + GF capture) =====
    initDiagnostic(prefersReducedMotion);

    // ===== Blog Index topic filter =====
    initBlogFilter();

    // ===== Scroll reveals =====
    // .reveal starts `visibility: hidden` in CSS; GSAP's autoAlpha tween drives
    // opacity + visibility together via inline styles, then y settles it into
    // place. Under reduced motion we skip the tween entirely and just show
    // the element in its resting position (no rise, no fade).
    document.querySelectorAll('.reveal').forEach(el => {
        if (prefersReducedMotion) {
            gsap.set(el, { autoAlpha: 1, y: 0 });
            return;
        }

        const delayMatch = el.className.match(/reveal-delay-(\d+)/);
        const delay = delayMatch ? parseInt(delayMatch[1]) * 0.15 : 0;

        gsap.fromTo(el,
            { autoAlpha: 0, y: 28 },
            {
                autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out',
                delay,
                scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' }
            }
        );
    });

    // Recalculate ScrollTrigger positions after webfonts and images settle,
    // otherwise reveals below the fold can stay hidden when initial layout
    // shifts (e.g. font swap, lazy-loaded images).
    window.addEventListener('load', () => ScrollTrigger.refresh());
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
    // Safety net: if any .reveal element is still hidden 2s after load (because
    // ScrollTrigger never fired for it), force-show it. Matches the "always
    // visible" intent (the fade is a nicety, not a gate).
    window.addEventListener('load', () => {
        setTimeout(() => {
            document.querySelectorAll('.reveal').forEach(el => {
                const cs = getComputedStyle(el);
                if (cs.visibility === 'hidden') {
                    gsap.set(el, { autoAlpha: 1, y: 0 });
                }
            });
        }, 2000);
    });

    // ===== Hero parallax =====
    // Images flagged [data-hero-parallax] (the hero block's "Parallax" toggle)
    // drift slower than the page as their section scrolls out. The image is
    // scaled up slightly so the vertical drift never exposes a gap at the edges
    // of the (overflow-hidden) hero section. Skipped under reduced motion.
    if (!prefersReducedMotion) {
        document.querySelectorAll('[data-hero-parallax]').forEach(img => {
            const section = img.closest('section') || img.parentElement;
            gsap.set(img, { scale: 1.18 });
            gsap.fromTo(img,
                { yPercent: -8 },
                {
                    yPercent: 8, ease: 'none',
                    scrollTrigger: {
                        trigger: section,
                        start: 'top top',
                        end: 'bottom top',
                        scrub: true,
                    },
                }
            );
        });
    }

    // ===== Full-screen overlay menu =====
    // Hooks emitted by the header template: [data-awdd-menu-toggle] is the
    // Menu/Close button, #awdd-overlay-menu is the panel. Both are optional
    // here since this ships ahead of the header markup that uses them.
    const overlayToggle = document.querySelector('[data-awdd-menu-toggle]');
    const overlayMenu = document.getElementById('awdd-overlay-menu');

    if (overlayToggle && overlayMenu) {
        const setOverlayOpen = (open) => {
            overlayMenu.classList.toggle('is-open', open);
            overlayToggle.setAttribute('aria-expanded', String(open));
            document.body.style.overflow = open ? 'hidden' : '';
        };

        overlayToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            setOverlayOpen(!overlayMenu.classList.contains('is-open'));
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && overlayMenu.classList.contains('is-open')) {
                setOverlayOpen(false);
                overlayToggle.focus();
            }
        });

        // Close on link click so navigating within the overlay doesn't leave
        // it open behind the next page.
        overlayMenu.querySelectorAll('a').forEach(a => {
            a.addEventListener('click', () => setOverlayOpen(false));
        });
    }

    // ===== Accordions =====
    // [data-awdd-accordion] wraps a toggle button and a [data-awdd-accordion-panel].
    // Used for the Services/Company groups inside the overlay menu, but works
    // anywhere the markup shows up. Panels animate via max-height + opacity
    // rather than display:none so the transition can run.
    document.querySelectorAll('[data-awdd-accordion]').forEach(accordion => {
        const toggle = accordion.querySelector('button');
        const panel = accordion.querySelector('[data-awdd-accordion-panel]');
        if (!toggle || !panel) return;

        panel.style.overflow = 'hidden';
        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';
        if (!prefersReducedMotion) {
            panel.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
        }
        toggle.setAttribute('aria-expanded', 'false');

        toggle.addEventListener('click', () => {
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            const nextOpen = !isOpen;

            toggle.setAttribute('aria-expanded', String(nextOpen));
            accordion.classList.toggle('is-open', nextOpen);
            panel.style.maxHeight = nextOpen ? panel.scrollHeight + 'px' : '0px';
            panel.style.opacity = nextOpen ? '1' : '0';
        });

        // Keep an open panel's max-height correct if content reflows
        // (viewport resize, webfont swap).
        window.addEventListener('resize', () => {
            if (toggle.getAttribute('aria-expanded') === 'true') {
                panel.style.maxHeight = panel.scrollHeight + 'px';
            }
        }, { passive: true });
    });

    // ===== Count-up stats =====
    // [data-countup] elements hold their final value as text content, which
    // may carry a non-numeric prefix/suffix (e.g. "180+", "29 yrs"). Values
    // with no leading number (e.g. "ISO 27001") are left untouched.
    if ('IntersectionObserver' in window) {
        document.querySelectorAll('[data-countup]').forEach(el => {
            const target = el.textContent.trim();
            const match = target.match(/^([^\d]*)([\d.,]+)(.*)$/);
            // Bail if there's no numeric part, or if the leading text has letters
            // in it (e.g. "ISO 27001"), since that's a label, not a number with a unit.
            if (!match || /[a-z]/i.test(match[1])) return;

            if (prefersReducedMotion) {
                el.textContent = target;
                return;
            }

            const [, prefix, numRaw, suffix] = match;
            const decimals = (numRaw.split('.')[1] || '').length;
            const end = parseFloat(numRaw.replace(/,/g, ''));
            const hasComma = numRaw.includes(',');
            const duration = 1200;

            const io = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return;
                    io.unobserve(entry.target);

                    const start = performance.now();
                    const tick = (now) => {
                        const t = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - t, 3);
                        const shown = (end * eased).toFixed(decimals);
                        const formatted = hasComma ? Number(shown).toLocaleString('en-AU') : shown;
                        el.textContent = `${prefix}${formatted}${suffix}`;
                        if (t < 1) {
                            requestAnimationFrame(tick);
                        } else {
                            el.textContent = target;
                        }
                    };
                    requestAnimationFrame(tick);
                });
            }, { threshold: 0.4 });

            io.observe(el);
        });
    }

    // ===== Case study industry filter =====
    // Progressive enhancement for the case-grid block: chips carry data-filter
    // ("all" or an industry name), cards carry data-industry. With JS off every
    // card is visible. Toggling uses the `hidden` attribute so hidden cards leave
    // the flow; ScrollTrigger is refreshed so any reveals below settle correctly.
    document.querySelectorAll('[data-awdd-case-filter]').forEach(root => {
        const chips = root.querySelectorAll('[data-filter]');
        const cards = root.querySelectorAll('[data-industry]');
        if (!chips.length) return;

        chips.forEach(chip => {
            chip.addEventListener('click', () => {
                const filter = chip.getAttribute('data-filter');
                chips.forEach(c => c.setAttribute('aria-pressed', String(c === chip)));
                cards.forEach(card => {
                    const show = filter === 'all' || card.getAttribute('data-industry') === filter;
                    card.hidden = !show;
                });
                ScrollTrigger.refresh();
            });
        });
    });

    // ===== Smooth scroll =====
    document.querySelectorAll('a[href^="#"]').forEach(a => {
        a.addEventListener('click', function (e) {
            const t = document.querySelector(this.getAttribute('href'));
            if (t) {
                e.preventDefault();
                t.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    });

    /* ── Scroll to top button ── */
    const scrollTopBtn = document.getElementById('scrollTopBtn');
    if (scrollTopBtn) {
        window.addEventListener('scroll', () => {
            scrollTopBtn.classList.toggle('visible', window.scrollY > 400);
        }, { passive: true });
        scrollTopBtn.addEventListener('click', () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }

    /* ── Custom cursor (fine pointer / desktop only) ── */
    (function initCursor() {
        if (!window.matchMedia('(pointer: fine)').matches || window.innerWidth < 768) return;

        const dot = document.createElement('div');
        const ring = document.createElement('div');
        dot.className = 'cursor-dot';
        ring.className = 'cursor-ring';
        document.body.append(dot, ring);

        let mx = -100, my = -100;
        let rx = -100, ry = -100;

        document.addEventListener('mousemove', e => {
            mx = e.clientX;
            my = e.clientY;
            dot.style.transform = `translate(calc(${mx}px - 50%), calc(${my}px - 50%))`;
        });

        (function raf() {
            rx += (mx - rx) * 0.1;
            ry += (my - ry) * 0.1;
            ring.style.transform = `translate(calc(${rx}px - 50%), calc(${ry}px - 50%))`;
            requestAnimationFrame(raf);
        })();

        const interactives = 'a, button, [role="button"], label, input, select, textarea';
        document.addEventListener('mouseover', e => {
            if (e.target.closest(interactives)) ring.classList.add('hovered');
        });
        document.addEventListener('mouseout', e => {
            if (e.target.closest(interactives)) ring.classList.remove('hovered');
        });

        document.addEventListener('mousedown', () => {
            ring.classList.add('clicking');
            dot.classList.add('hidden');
        });
        document.addEventListener('mouseup', () => {
            ring.classList.remove('clicking');
            dot.classList.remove('hidden');
        });

        document.addEventListener('mouseleave', () => {
            dot.style.opacity = '0';
            ring.style.opacity = '0';
        });
        document.addEventListener('mouseenter', () => {
            dot.style.opacity = '1';
            ring.style.opacity = '1';
        });
    })();

});
